import { AnalyticsMessage, ToMapWorkerMessage } from './MapWorkerProtocol';

export const formatAnalyticsMessage = (
  { startTime, endTime, queueTime }: { startTime: number; endTime: number; queueTime: number },
  message: ToMapWorkerMessage,
  workerExtras: AnalyticsMessage['workerExtras']
): AnalyticsMessage => {
  const analyticsKind = getAnalyticsKind(message);
  const waitDuration = performance.timeOrigin + startTime - queueTime;
  const duration = endTime - startTime;
  const budget = performanceBudgets[analyticsKind] ?? Infinity;
  const percentOfBudget = (100 * duration) / budget;
  return {
    type: 'analytics',
    start: startTime,
    end: endTime,
    workerExtras,
    analyticsKind,
    messageExtras: formatMessageExtras(message),
    timing: {
      duration,
      budget,
      percentOfBudget,
      waitDuration
    }
  };
};

function formatMessageExtras(message: ToMapWorkerMessage): AnalyticsMessage['messageExtras'] {
  switch (message.type) {
    case 'geo-hierarchy-change':
      return {
        countrySources:
          message.bulkHierarchyPolygons?.inUseCatalogItems.map((c) => `${c.country}::${c.sourceKey}`) ?? [],
        totalPolygonCount: sum(message.bulkHierarchyPolygons?.inUseCatalogItems.map((c) => c.polygonCount) ?? [])
      };
    case 'rules-change':
      return {
        cause: message.cause,
        count: message.rules.length
      };
    case 'customer-pins-change':
      return {
        count: message.customerPins.length
      };
    case 'custom-hierarchy-filter-change':
      return {};
    case 'lasso-pins':
      return {
        count: message.accountIds.length
      };
    case 'lasso-polygons':
      return {
        count: message.polygonsInBounds.length
      };

    case 'segments-change':
      return {
        count: message.segments?.length
      };

    default:
      return null;
  }
}

const sum = (values: number[]) => {
  let total = 0;
  for (const value of values) total += value;
  return total;
};

const getAnalyticsKind = (message: ToMapWorkerMessage) => {
  if (message.type === 'question') return `${message.type}::${message.questionKind}` as const;
  return message.type;
};

type AnalyticsKind = ReturnType<typeof getAnalyticsKind>;

const Times = {
  small: 100,
  medium: 250,
  large: 500,
  xLarge: 1_000,
  xxLarge: 2000,
  unbounded: Infinity
};

const performanceBudgets: Record<AnalyticsKind, number> = {
  'get-managed-state': Times.small,
  'get-initial-selected-accounts': Times.small,
  'toggle-rules-locked': Times.small,
  'click-pin': Times.small,
  'click-polygon': Times.small,
  'clear-selection': Times.medium,
  'set-selected-rules': Times.medium,
  'lasso-polygons': Times.medium,
  'lasso-pins': Times.medium,
  'customer-pins-change': Times.medium,
  'toggle-rules-ignored': Times.medium,
  'toggle-all-rules-locked': Times.medium,
  'toggle-all-rules-ignored': Times.medium,
  'isolate-rules': Times.medium,
  'un-isolate-rules': Times.medium,
  'group-lookup-change': Times.medium,
  'rules-change': Times.large,
  'custom-hierarchy-filter-change': Times.large,
  'question::get-details-for-create-rule': Times.large,
  'question::get-fix-conflict-details': Times.large,
  'question::get-names-for-unassign': Times.large,
  'question::initiate-undo': Times.large,
  'question::get-territory-counts-for-segments': Times.large,
  'question::get-territory-counts-for-geo-only-segment': Times.large,
  'question::get-custom-hierarchies-in-territory-rule': Times.small,
  'geo-hierarchy-change': Times.xxLarge,
  'segments-change': Times.small,
  echo: Times.unbounded
};

export const logAnalyticsToDevtools = (message: AnalyticsMessage) => {
  performance.measure(message.analyticsKind, {
    start: message.start,
    end: message.end,
    detail: {
      devtools: {
        dataType: 'track-entry',
        track: 'Map Worker',
        color: message.timing.percentOfBudget <= 100 ? 'primary' : 'secondary',
        properties: Object.entries({
          ...message.timing,
          ...message.workerExtras,
          ...message.messageExtras
        }).map(([key, value]) => [key, `${value}`])
      }
    }
  });
};
